import React, { CSSProperties, useContext, useRef, useState } from 'react';
import GameContext from 'contexts/GameContext';
import gsap from 'gsap';
import { useOrientation } from 'hooks/useOrientation';
import { useSupportsHover } from 'hooks/useSupportsHover';
import { saloonFonts } from 'styles/saloonTheme';

import ActionEffect from './ActionEffect';
import { ActionVariantKey, actionVariants, splitLabelAmount } from './actionVariants';

type ActionButtonProps = {
  onClick: () => void;
  disabled?: boolean;
  variant?: ActionVariantKey;
  // Retained for call-site compatibility. Icons are no longer rendered — the
  // action buttons are text-only (label + amount) per the EN-10265 design.
  icon?: 'fold' | 'check' | 'call' | 'raise' | 'allin';
  // Whether this click commits a poker action (Fold/Check/Call/All-In or a
  // raise-amount confirmation). When false, the click is a UI affordance
  // only (e.g. opening the raise slider panel) and the player's turn timer
  // must remain visible. Defaults to true so existing call sites keep their
  // pre-existing "hide timer on click" behavior.
  committing?: boolean;
  /**
   * 'decision' is the multi-run vote's decision panel (EN-10856): the same
   * button, taller and set larger, because it is the one decision on the table.
   * Every other caller keeps the default row size.
   */
  size?: 'default' | 'decision';
  // Portrait: take half the row, up to this width, instead of sharing all of it.
  // EN-11023: the end-of-hand row (Show/Muck Cards and the straddle chip, see
  // CheckboxAction's maxWidth) splits 50/50, and a lone button sits centred
  // rather than spanning the bar. They share HAND_END_BUTTON_MAX_WIDTH.
  maxWidth?: string;
  children: React.ReactNode;
};

/** The widest a Show/Muck Cards button or the straddle chip grows (portrait). */
export const HAND_END_BUTTON_MAX_WIDTH = '20rem';

const SALOON_BOX_SHADOW = '0 4px 10px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,220,160,0.1)';

const ActionButton = ({
  onClick,
  disabled = false,
  variant = 'check',
  committing = true,
  size = 'default',
  maxWidth,
  children
}: ActionButtonProps) => {
  const isDecision = size === 'decision';
  const s = actionVariants[variant] || actionVariants.check;
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const supportsHover = useSupportsHover();
  const { setIsTimerOff } = useContext(GameContext);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [effectTrigger, setEffectTrigger] = useState(0);

  const handleClick = () => {
    if (disabled) return;

    // Trigger particle effect
    setEffectTrigger((prev) => prev + 1);

    // Only hide the timer ring when the click actually commits an action.
    // Opening the raise slider panel is a non-committing click, so the
    // timer must keep ticking until the player confirms the amount.
    if (committing) {
      setIsTimerOff(true);
    }

    // Button press animation
    if (btnRef.current) {
      gsap
        .timeline()
        .to(btnRef.current, { scale: 0.9, duration: 0.08, ease: 'power2.in' })
        .to(btnRef.current, { scale: 1.05, duration: 0.12, ease: 'back.out(3)' })
        .to(btnRef.current, { scale: 1, duration: 0.1, ease: 'power2.out' });
    }

    // Fire the action
    onClick();
  };

  // Text-only buttons: split "Call $0.38" / "All In 1,200" into a bold label
  // line + a smaller amount line. Plain labels (Fold / Check / Bet / Raise)
  // have no amount and render as a single centered line.
  const text = typeof children === 'string' ? children : '';
  const { label, amount } = text ? splitLabelAmount(text) : { label: '', amount: '' };

  const style: CSSProperties = {
    position: 'relative',
    overflow: 'visible',
    background: s.bg,
    border: `1px solid ${s.border}`,
    borderRadius: '6px',
    color: disabled ? 'rgba(241,227,199,0.35)' : s.text,
    // One number for every button in the row, deliberately. This was a `paddingX`
    // prop that only the EN-663 vote row ever passed, to widen itself against the
    // action row it stands in place of; the vote row now matches instead.
    padding: isDecision
      ? isPortrait
        ? '0 0.5rem'
        : '0 2rem'
      : isPortrait
      ? '0 0.35rem'
      : '0 1.5rem',
    // Match the pre-action (preselect) chips' typeface — 'Oswald' — so the
    // action row and the pre-action row read as one type system.
    fontFamily: saloonFonts.numeric, // 'Oswald'
    textShadow: s.shadow,
    cursor: disabled ? 'default' : 'pointer',
    whiteSpace: 'nowrap',
    opacity: disabled ? 0.5 : 1,
    // Animate transform ONLY — box-shadow/background stay static (perf guide).
    transition: 'transform 0.12s ease',
    boxShadow: SALOON_BOX_SHADOW,
    display: 'flex',
    // Stack the label over the amount (two-line action-button design).
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1px',
    lineHeight: 1.05,
    // Portrait: `1 1 auto` (basis = content width) instead of `1` (basis 0) so
    // buttons start at their natural label width and SHARE the leftover space.
    flex: isPortrait ? (maxWidth ? '0 1 50%' : '1 1 auto') : undefined,
    minWidth: 0,
    maxWidth: isPortrait ? maxWidth : undefined,
    // Portrait: shorter (48px) so the action row aligns with the 44px history/
    // chat icons now sharing the footer; landscape unchanged.
    minHeight: isDecision ? '60px' : '48px'
  };

  // Sizes match the ORIGINAL action button (0.82/0.9rem, 0.04em tracking) — the
  // screenshots are the reference for STYLE (weight, colour, stacked amount, no
  // icons), not literal dimensions. Weight is bumped to 700 for the bold look.
  const labelStyle: CSSProperties = {
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: isDecision ? '0.06em' : '0.04em',
    fontSize: isDecision ? (isPortrait ? '1.05rem' : '1.15rem') : isPortrait ? '0.82rem' : '0.9rem'
  };

  const amountStyle: CSSProperties = {
    fontWeight: 600,
    letterSpacing: '0.02em',
    fontSize: isPortrait ? '0.7rem' : '0.76rem',
    opacity: 0.95
  };

  // Hover is desktop-only: on iOS WKWebView the synthesized mouseenter eats
  // the first tap. Gate it behind useSupportsHover and only nudge transform.
  const hoverHandlers = supportsHover
    ? {
        onMouseEnter: (e: React.MouseEvent) => {
          if (!disabled) (e.currentTarget as HTMLElement).style.transform = 'scale(1.04)';
        },
        onMouseLeave: (e: React.MouseEvent) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
        }
      }
    : {};

  return (
    <button ref={btnRef} onClick={handleClick} style={style} disabled={disabled} {...hoverHandlers}>
      {/* Static inset dashed "stitch" edge — the design's ::before, as a
          composited overlay (no animation, pointer-events off). */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          inset: '3px',
          border: `1px dashed ${s.dash}`,
          borderRadius: '4px',
          pointerEvents: 'none'
        }}
      />
      {text ? (
        <>
          <span style={labelStyle}>{label}</span>
          {amount ? <span style={amountStyle}>{amount}</span> : null}
        </>
      ) : (
        children
      )}
      <ActionEffect variant={variant || 'check'} trigger={effectTrigger} />
    </button>
  );
};

export default ActionButton;
